import { useTranslation } from 'react-i18next';

import { SegmentedControl } from '@/components/ui/segmented-control';

/**
 * All / Any, for a card whose conditions are a list.
 *
 * The radiogroup underneath is `SegmentedControl` — the app's 2-3-way inline switch, which already
 * carries the roving tabindex, the arrow keys and Home/End. Flow had its own copy of that markup,
 * one test short of the shared one; this file exists to give the shared control Flow's sentence
 * wrapper, not to re-implement it.
 *
 * The wording changed with the list. The grid made matchMode mean two things at once — AND within a
 * block's states, AND across blocks — so the control could only be labelled abstractly. A flat list
 * has one level, so the control can say what it does: these conditions, all or any.
 */
export function FlowMatchMode({ value, onChange }: { value: 'any' | 'all'; onChange: (mode: 'any' | 'all') => void }) {
  const { t } = useTranslation();
  // No leading word: the line above already says what happens ("Stay on this task"), so this one is
  // read as its condition rather than as a second sentence about firing.
  return (
    <div className='flex flex-wrap items-center gap-1.5 text-xs text-ws-muted'>
      <SegmentedControl
        value={value}
        onChange={(v) => onChange(v as 'any' | 'all')}
        aria-label={t('task.flow.card.matchModeLabel')}
        options={[
          { id: 'all', label: t('task.flow.card.all') },
          { id: 'any', label: t('task.flow.card.any') },
        ]}
      />
      <span>{t('task.flow.card.ofTheseAreTrue')}</span>
    </div>
  );
}
